import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, Empty } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UserCheck, Award, Check, X } from "lucide-react";
import { toast } from "sonner";

export default function Students() {
  const qc = useQueryClient();
  const [batchFilter, setBatchFilter] = useState("");
  const [detail, setDetail] = useState(null);

  const { data: batches } = useQuery({ queryKey: ["batches"], queryFn: async () => (await api.get("/batches")).data });
  const { data, isLoading } = useQuery({
    queryKey: ["students", batchFilter], queryFn: async () => (await api.get("/students", { params: { batch_id: batchFilter || undefined } })).data,
  });

  const today = new Date().toISOString().slice(0, 10);
  const markAttendance = useMutation({
    mutationFn: async ({ student_id, batch_id, present }) => (await api.post("/students/attendance", { student_id, batch_id, date: today, present })).data,
    onSuccess: () => { toast.success("Attendance saved"); qc.invalidateQueries(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const issueCert = useMutation({
    mutationFn: async (sid) => (await api.post(`/students/${sid}/certificate`)).data,
    onSuccess: (d) => toast.success(`Certificate ${d.cert_no} issued`),
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <div>
      <PageHeader title="Students" subtitle="Rosters, attendance & completion"
        actions={
          <Select value={batchFilter || "all"} onValueChange={(v) => setBatchFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-56" data-testid="student-batch-filter"><SelectValue placeholder="All batches" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All batches</SelectItem>{(batches || []).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
          </Select>
        } testid="students-header" />

      {isLoading ? <Loading /> : !data?.length ? <Empty title="No students yet" subtitle="Students appear here after admission." /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.map((s) => (
            <div key={s.id} className="p-4 rounded-xl border bg-white dark:bg-slate-800" data-testid={`student-${s.id}`}>
              <div className="flex items-center justify-between">
                <div><div className="font-semibold">{s.name}</div><div className="text-xs text-slate-500 font-mono">{s.student_no}</div></div>
                <Badge variant="secondary" className="text-[10px]">{s.status}</Badge>
              </div>
              <div className="text-sm text-slate-500 mt-2">{s.course_name || "—"}</div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" className="gap-1 flex-1" onClick={() => markAttendance.mutate({ student_id: s.id, batch_id: s.batch_id, present: true })} data-testid={`att-present-${s.id}`}><Check className="w-4 h-4 text-emerald-600" /> Present</Button>
                <Button size="sm" variant="outline" className="gap-1 flex-1" onClick={() => markAttendance.mutate({ student_id: s.id, batch_id: s.batch_id, present: false })} data-testid={`att-absent-${s.id}`}><X className="w-4 h-4 text-red-600" /> Absent</Button>
              </div>
              <div className="flex gap-2 mt-2">
                <Button size="sm" variant="ghost" className="flex-1" onClick={() => api.get(`/students/${s.id}`).then((r) => setDetail(r.data))} data-testid={`student-view-${s.id}`}>Details</Button>
                <Button size="sm" variant="ghost" className="gap-1 flex-1 text-brand-blue" onClick={() => issueCert.mutate(s.id)} data-testid={`cert-${s.id}`}><Award className="w-4 h-4" /> Certificate</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {detail && (
        <Dialog open onOpenChange={() => setDetail(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>{detail.name} • {detail.student_no}</DialogTitle></DialogHeader>
            <div className="text-sm space-y-2">
              <p className="text-slate-500">Course: {detail.course_name || "—"}</p>
              <div className="font-semibold mt-2">Attendance ({detail.attendance?.length || 0})</div>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {(detail.attendance || []).map((a) => <div key={a.id} className="flex justify-between text-xs border-b py-1"><span>{a.date}</span><span className={a.present ? "text-emerald-600" : "text-red-600"}>{a.present ? "Present" : "Absent"}{a.corrected ? " (corrected)" : ""}</span></div>)}
                {!(detail.attendance || []).length && <p className="text-xs text-slate-400">No attendance yet</p>}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
